import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DateTimePicker from "@/components/ui/date-time-picker";
import { toast } from "sonner";
import { Plus, Check, Pencil, X, Trash2, Link2, MapPin, Clock } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { SCHEDULED_ACTIVITY_TYPES } from "@/lib/scheduledActivities";

// 3 kartu terjadwal ala Trello di tab Aktivitas — beda dari log aktivitas bebas (catatan
// teks di bawahnya) dan beda juga dari otomasi tahap Survei di Pipeline (yang jalan dari
// follow_up_date). Ini murni jadwal ad-hoc: tanggal+jam nyata, pengingat otomatis
// (H-1/hari-H/nudge harian, ditangani backend), tidak pernah mengubah tahap Pipeline —
// kecuali Follow Up, yang sinkron satu arah ke lead.follow_up_date (lihat catatan di
// lib/scheduledActivities.js & _sync_lead_followup_date di backend).
// Definisi tipe (ikon/label/warna) di-share lewat lib/scheduledActivities — dipakai juga
// oleh badge kartu Pipeline & widget Dashboard supaya tidak ada 3 sumber kebenaran beda.
const TYPES = Object.entries(SCHEDULED_ACTIVITY_TYPES).map(([key, v]) => ({ key, ...v }));

const blankForm = (type) => ({ activity_id: null, type, scheduled_at: "", note: "", meeting_link: "", maps_link: "" });

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ScheduledActivityBoard({ lead, leadId, refresh }) {
  const [form, setForm] = useState(null); // null = dialog closed
  const [saving, setSaving] = useState(false);
  const items = lead.scheduled_activities || [];
  const now = Date.now();

  const openAdd = (type) => setForm(blankForm(type));
  const openEdit = (a) => setForm({ activity_id: a.activity_id, type: a.type, scheduled_at: toLocalInput(a.scheduled_at), note: a.note || "", meeting_link: a.meeting_link || "", maps_link: a.maps_link || "" });

  const save = async () => {
    if (!form.scheduled_at) { toast.error("Tanggal & jam wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { type: form.type, scheduled_at: new Date(form.scheduled_at).toISOString(), note: form.note, meeting_link: form.meeting_link, maps_link: form.maps_link };
      if (form.activity_id) await api.put(`/leads/${leadId}/scheduled-activities/${form.activity_id}`, payload);
      else await api.post(`/leads/${leadId}/scheduled-activities`, payload);
      toast.success(form.activity_id ? "Jadwal diperbarui" : "Jadwal ditambahkan");
      setForm(null); refresh();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const complete = async (a) => {
    try { await api.post(`/leads/${leadId}/scheduled-activities/${a.activity_id}/complete`); toast.success("Ditandai selesai"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const cancel = async (a) => {
    if (!window.confirm("Batalkan jadwal ini?")) return;
    try { await api.post(`/leads/${leadId}/scheduled-activities/${a.activity_id}/cancel`); toast.success("Jadwal dibatalkan"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (a) => {
    if (!window.confirm("Hapus jadwal ini permanen?")) return;
    try { await api.delete(`/leads/${leadId}/scheduled-activities/${a.activity_id}`); toast.success("Jadwal dihapus"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-3 mb-4">
      {TYPES.map((t) => {
        const Icon = t.icon;
        const cards = items.filter((a) => a.type === t.key)
          .slice().sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        return (
          <div key={t.key} className="rounded-xl border border-white/10 bg-[#18181B] overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <Icon size={15} style={{ color: t.accent }} />{t.label}
                {cards.length > 0 && <span className="text-[11px] text-zinc-500">({cards.length})</span>}
              </div>
              <button data-testid={`sched-add-${t.key}`} onClick={() => openAdd(t.key)}
                className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md text-blue-300 hover:bg-blue-500/10">
                <Plus size={13} />Jadwalkan
              </button>
            </div>
            <div className="p-2.5 space-y-2">
              {cards.length === 0 && <p className="text-[11px] text-zinc-600 text-center py-2">Belum ada jadwal {t.label.toLowerCase()}</p>}
              {cards.map((a) => {
                const sched = new Date(a.scheduled_at).getTime();
                const overdue = a.status === "pending" && sched < now;
                const overdueDays = overdue ? Math.floor((now - sched) / 86400000) : 0;
                return (
                  <div key={a.activity_id} data-testid={`sched-card-${a.activity_id}`}
                    className={`rounded-lg border p-2.5 text-xs space-y-1.5 ${
                      a.status === "done" ? "bg-emerald-500/5 border-emerald-500/15 opacity-70"
                      : a.status === "cancelled" ? "bg-white/[0.02] border-white/5 opacity-50"
                      : overdue ? "bg-red-500/5 border-red-500/20" : "bg-[#0f0f12] border-white/5"
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`flex items-center gap-1 font-medium ${overdue ? "text-red-400" : ""}`}>
                        <Clock size={11} />{fmtDateTime(a.scheduled_at)}
                      </span>
                      {a.status === "done" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Selesai</span>}
                      {a.status === "cancelled" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-600/20 text-zinc-500">Dibatalkan</span>}
                      {overdue && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">Telat {overdueDays}h</span>}
                    </div>
                    {a.note && <p className="text-zinc-400 break-words">{a.note}</p>}
                    {a.meeting_link && (
                      <a href={a.meeting_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline break-all">
                        <Link2 size={11} />{a.meeting_link}
                      </a>
                    )}
                    {a.maps_link && (
                      <a href={a.maps_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-purple-400 hover:underline break-all">
                        <MapPin size={11} />{a.maps_link}
                      </a>
                    )}
                    <p className="text-[10px] text-zinc-600">Dibuat {a.created_by}{a.status === "done" && a.completed_by ? ` · Selesai oleh ${a.completed_by}` : ""}</p>
                    {a.status === "pending" && (
                      <div className="flex items-center gap-1 pt-0.5">
                        <button data-testid={`sched-complete-${a.activity_id}`} onClick={() => complete(a)}
                          className="flex items-center gap-1 text-[10.5px] px-1.5 py-1 rounded-md text-emerald-400 hover:bg-emerald-500/10"><Check size={11} />Selesai</button>
                        <button data-testid={`sched-edit-${a.activity_id}`} onClick={() => openEdit(a)}
                          className="flex items-center gap-1 text-[10.5px] px-1.5 py-1 rounded-md text-zinc-400 hover:bg-white/5"><Pencil size={11} />Edit</button>
                        <button data-testid={`sched-cancel-${a.activity_id}`} onClick={() => cancel(a)}
                          className="flex items-center gap-1 text-[10.5px] px-1.5 py-1 rounded-md text-amber-400 hover:bg-amber-500/10"><X size={11} />Batal</button>
                      </div>
                    )}
                    {a.status !== "pending" && a.status !== "done" && (
                      <div className="flex items-center gap-1 pt-0.5">
                        <button data-testid={`sched-delete-${a.activity_id}`} onClick={() => del(a)}
                          className="flex items-center gap-1 text-[10.5px] px-1.5 py-1 rounded-md text-red-400 hover:bg-red-500/10"><Trash2 size={11} />Hapus</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-[#0f0f12] border-white/10">
          <DialogHeader><DialogTitle className="font-heading">{form?.activity_id ? "Ubah Jadwal" : "Jadwalkan"} {TYPES.find((t) => t.key === form?.type)?.label}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-zinc-400 uppercase">Tanggal & Jam</Label>
                <div className="mt-1">
                  <DateTimePicker testId="sched-datetime" value={form.scheduled_at}
                    onChange={(v) => setForm({ ...form, scheduled_at: v })} />
                </div>
              </div>
              {form.type === "zoom_meeting" && (
                <div>
                  <Label className="text-xs text-zinc-400 uppercase">Link Meeting</Label>
                  <Input data-testid="sched-link" placeholder="https://zoom.us/j/..." value={form.meeting_link}
                    onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" />
                </div>
              )}
              {form.type === "survei" && (
                <div>
                  <Label className="text-xs text-zinc-400 uppercase">Link Google Maps</Label>
                  <Input data-testid="sched-maps-link" placeholder="https://maps.app.goo.gl/..." value={form.maps_link}
                    onChange={(e) => setForm({ ...form, maps_link: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" />
                </div>
              )}
              <div>
                <Label className="text-xs text-zinc-400 uppercase">Catatan (opsional)</Label>
                <Textarea data-testid="sched-note" rows={2} value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-[#18181B] border-white/10 mt-1" />
              </div>
              <Button data-testid="sched-save" onClick={save} disabled={saving} className="w-full h-9 bg-blue-600 hover:bg-blue-700">
                {saving ? "Menyimpan…" : "Simpan Jadwal"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
